import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Grid, Alert, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, Skeleton,
} from '@mui/material';
import {
  fetchLiveSessions, scheduleLiveSession, LiveSession, NewLiveSession,
} from '../../services/proctoringService';

/**
 * Live review session scheduler (#07), teacher side.
 *
 * Scheduling is admin-and-entitlement gated server-side. This form does not try to
 * predict either: it submits and reports what the server said, because a client
 * that greys out its own button on a guess at the caller's role gets it wrong the
 * first time roles change, and then nobody can schedule anything.
 */
const emptyForm: NewLiveSession = {
  title: '',
  description: '',
  startsAt: '',
  durationMinutes: 60,
  capacity: 30,
  joinUrl: '',
};

const LiveScheduler: React.FC = () => {
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'locked' | 'error'>('loading');
  const [form, setForm] = useState<NewLiveSession>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    const result = await fetchLiveSessions();
    if (result.state === 'ready') { setSessions(result.sessions); setState('ready'); }
    else if (result.state === 'locked') setState('locked');
    else { setState('error'); setError(result.message ?? null); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true); setError(null); setSaved(false);
    const failure = await scheduleLiveSession({
      ...form,
      // datetime-local has no zone; treat it as local wall-clock, which is what the
      // teacher typing it means, and send an instant the server can store.
      startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : '',
    });
    if (failure) setError(failure);
    else { setSaved(true); setForm(emptyForm); await load(); }
    setSaving(false);
  };

  if (state === 'loading') return <Skeleton variant="rounded" height={260} />;
  if (state === 'locked') {
    return <Alert severity="info">Live review sessions are part of a paid plan for this school.</Alert>;
  }

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Schedule a live review</Typography>
          {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
          {saved && <Alert severity="success" sx={{ mb: 2 }}>Session scheduled.</Alert>}

          <Box component="form" onSubmit={submit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth required label="Title" value={form.title}
                           onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth required label="Starts at" type="datetime-local"
                           InputLabelProps={{ shrink: true }} value={form.startsAt}
                           onChange={(e) => setForm({ ...form, startsAt: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField fullWidth label="Minutes" type="number" inputProps={{ min: 10, max: 240 }}
                           value={form.durationMinutes}
                           onChange={(e) => setForm({ ...form, durationMinutes: Number(e.target.value) })} />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField fullWidth label="Capacity" type="number" inputProps={{ min: 1 }}
                           value={form.capacity}
                           onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="Join link" placeholder="https://…" value={form.joinUrl}
                           onChange={(e) => setForm({ ...form, joinUrl: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth label="Description" multiline rows={2} value={form.description}
                           onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Grid>
            </Grid>
            <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={saving}>
              {saving ? 'Scheduling…' : 'Schedule'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Upcoming</Typography>
          {sessions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Nothing scheduled yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Starts</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Enrolled</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>{s.title}</TableCell>
                    <TableCell>{new Date(s.startsAt).toLocaleString()}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={s.status} /></TableCell>
                    <TableCell align="right">
                      {s.enrolledCount}{s.capacity ? ` / ${s.capacity}` : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LiveScheduler;
